import { useTranslation } from 'react-i18next';
import { useGetFeedback } from '@/shared/hooks/useInteractions';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { FeedbackStatus } from '@momentum/types';
import { FeedbackBlockageContent } from './FeedbackBlockageContent';
import { FeedbackReviewContent } from './FeedbackReviewContent';

interface FeedbackContentProps {
  feedbackId: string;
  onBack?: () => void;
}

export const FeedbackContent = ({
  feedbackId,
  onBack,
}: FeedbackContentProps) => {
  const { t } = useTranslation('common');
  const currentEmployee = useEmployeeContext();

  const { data: feedback, isLoading } = useGetFeedback(feedbackId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">{t('loadingData')}</p>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">{t('notFound')('Feedback')}</p>
        </div>
      </div>
    );
  }

  // Determine which view to show based on user role and feedback status
  const isReceiver = feedback.receiverId === currentEmployee._id;
  const isSender = feedback.creatorId === currentEmployee._id;
  const shouldShowBlockage =
    isReceiver || (isSender && feedback.status !== FeedbackStatus.SENT);

  // Show blockage/action plan view
  if (shouldShowBlockage) {
    return <FeedbackBlockageContent feedbackId={feedbackId} />;
  }

  // Show review view (sender viewing SENT feedback)
  return <FeedbackReviewContent feedbackId={feedbackId} onBack={onBack} />;
};
